import React from "react";

import { Institution, InstitutionDraft } from "../../models/institution.model";
import {
  createInstitutionRequest,
  getInstitutionsRequest,
  updateInstitutionRequest,
} from "./store/admin.api";
import styles from "./admin-theme.module.css";

const EMPTY_INSTITUTION_DRAFT: InstitutionDraft = {
  name: "",
  edmProviderName: "",
  sketchfabImportLimit: 10,
  active: true,
};

const CREATE_INSTITUTION_NAME_ID = "institutions-create-name";
const CREATE_INSTITUTION_PROVIDER_ID = "institutions-create-provider";
const CREATE_INSTITUTION_LIMIT_ID = "institutions-create-limit";

const toInstitutionItems = (response: any): Institution[] => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

const toInstitutionDraft = (institution: Institution): InstitutionDraft => ({
  name: institution.name,
  edmProviderName: institution.edmProviderName,
  sketchfabImportLimit: institution.sketchfabImportLimit,
  active: institution.active !== false,
});

const draftMatchesInstitution = (draft: InstitutionDraft, institution: Institution): boolean =>
  draft.name === institution.name &&
  draft.edmProviderName === institution.edmProviderName &&
  draft.sketchfabImportLimit === institution.sketchfabImportLimit &&
  draft.active === (institution.active !== false);

export const InstitutionsPage = () => {
  const [institutions, setInstitutions] = React.useState<Institution[]>([]);
  const [drafts, setDrafts] = React.useState<Record<string, InstitutionDraft>>({});
  const [createDraft, setCreateDraft] = React.useState<InstitutionDraft>(EMPTY_INSTITUTION_DRAFT);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const loadInstitutions = React.useCallback(async () => {
    setLoading(true);
    const response = await getInstitutionsRequest();
    setLoading(false);

    if (!response.data?.status) {
      setError(response.data?.msg || "Failed to load institutions");
      setInstitutions([]);
      return;
    }

    setError(null);
    setInstitutions(toInstitutionItems(response));
  }, []);

  React.useEffect(() => {
    loadInstitutions();
  }, [loadInstitutions]);

  React.useEffect(() => {
    setDrafts((current) => {
      const nextDrafts: Record<string, InstitutionDraft> = {};
      institutions.forEach((institution) => {
        const existing = current[institution._id];
        nextDrafts[institution._id] =
          existing && !draftMatchesInstitution(existing, institution)
            ? existing
            : toInstitutionDraft(institution);
      });
      return nextDrafts;
    });
  }, [institutions]);

  const updateDraft = (institution: Institution, patch: Partial<InstitutionDraft>) => {
    setDrafts((current) => ({
      ...current,
      [institution._id]: {
        ...(current[institution._id] || toInstitutionDraft(institution)),
        ...patch,
      },
    }));
  };

  const handleCreate = async () => {
    setSavingId("new");
    setError(null);
    const response = await createInstitutionRequest(createDraft);
    setSavingId(null);

    if (!response.data?.status) {
      setError(response.data?.msg || response.data?.error?.message || "Failed to create institution");
      return;
    }

    setCreateDraft(EMPTY_INSTITUTION_DRAFT);
    await loadInstitutions();
  };

  const handleSave = async (institution: Institution) => {
    const draft = drafts[institution._id] || toInstitutionDraft(institution);

    setSavingId(institution._id);
    setError(null);
    const response = await updateInstitutionRequest(institution._id, {
      ...draft,
      slug: institution.slug,
    });
    setSavingId(null);

    if (!response.data?.status) {
      setError(response.data?.msg || response.data?.error?.message || "Failed to update institution");
      return;
    }

    await loadInstitutions();
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Institutions</h3>
          <div className="text-white-50 small">
            Manage the canonical institution identity, EDM provider name, and shared Sketchfab SRS limit. Provider is required for exportable EDM records.
          </div>
        </div>
      </div>

      {error ? <div className="text-danger small mb-3">{error}</div> : null}

      <div className="card bg-dark text-white border-secondary mb-4">
        <div className="card-body">
          <div className="fw-semibold mb-3">Create Institution</div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor={CREATE_INSTITUTION_NAME_ID} className="form-label text-white-50 small">
                Institution Name
              </label>
              <input
                id={CREATE_INSTITUTION_NAME_ID}
                className={`form-control ${styles.lightInput}`}
                value={createDraft.name}
                onChange={(event) => setCreateDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor={CREATE_INSTITUTION_PROVIDER_ID} className="form-label text-white-50 small">
                Provider
              </label>
              <input
                id={CREATE_INSTITUTION_PROVIDER_ID}
                className={`form-control ${styles.lightInput}`}
                value={createDraft.edmProviderName}
                onChange={(event) =>
                  setCreateDraft((current) => ({ ...current, edmProviderName: event.target.value }))
                }
              />
            </div>
            <div className="col-md-2">
              <label htmlFor={CREATE_INSTITUTION_LIMIT_ID} className="form-label text-white-50 small">
                Shared SRS Limit
              </label>
              <input
                id={CREATE_INSTITUTION_LIMIT_ID}
                className={`form-control ${styles.lightInput}`}
                type="number"
                min={1}
                max={250}
                value={createDraft.sketchfabImportLimit}
                onChange={(event) =>
                  setCreateDraft((current) => ({
                    ...current,
                    sketchfabImportLimit: Number(event.target.value || 0),
                  }))
                }
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <div className="form-check mb-2">
                <input
                  id="institution-create-active"
                  className="form-check-input"
                  type="checkbox"
                  checked={createDraft.active}
                  onChange={(event) =>
                    setCreateDraft((current) => ({ ...current, active: event.target.checked }))
                  }
                />
                <label className="form-check-label" htmlFor="institution-create-active">
                  Active
                </label>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button
              className={`btn ${styles.darkActionButton}`}
              onClick={handleCreate}
              disabled={
                savingId === "new" ||
                createDraft.name.trim().length < 2 ||
                createDraft.edmProviderName.trim().length < 2 ||
                !Number.isInteger(createDraft.sketchfabImportLimit) ||
                createDraft.sketchfabImportLimit < 1 ||
                createDraft.sketchfabImportLimit > 250
              }
            >
              Create Institution
            </button>
          </div>
        </div>
      </div>

      {loading ? <div className="text-white-50">Loading institutions...</div> : null}

      {!loading && (
        <div className="overflow-auto">
          <table className="table text-white table-striped table-hover table-responsive mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Provider</th>
                <th>Shared SRS Limit</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {institutions.map((institution) => {
                const draft = drafts[institution._id] || toInstitutionDraft(institution);
                const hasChanges =
                  draft.name !== institution.name ||
                  draft.edmProviderName !== institution.edmProviderName ||
                  draft.sketchfabImportLimit !== institution.sketchfabImportLimit ||
                  draft.active !== (institution.active !== false);

                return (
                  <tr key={institution._id}>
                    <td>
                      <input
                        aria-label={`Institution name ${institution.name}`}
                        className={`form-control form-control-sm ${styles.lightInput}`}
                        value={draft.name}
                        onChange={(event) => updateDraft(institution, { name: event.target.value })}
                        disabled={savingId === institution._id}
                      />
                    </td>
                    <td>{institution.slug}</td>
                    <td>
                      <input
                        aria-label={`Provider ${institution.name}`}
                        className={`form-control form-control-sm ${styles.lightInput}`}
                        value={draft.edmProviderName}
                        onChange={(event) =>
                          updateDraft(institution, { edmProviderName: event.target.value })
                        }
                        disabled={savingId === institution._id}
                      />
                    </td>
                    <td>
                      <input
                        aria-label={`Shared SRS limit ${institution.name}`}
                        className={`form-control form-control-sm ${styles.lightInput}`}
                        type="number"
                        min={1}
                        max={250}
                        value={draft.sketchfabImportLimit}
                        onChange={(event) =>
                          updateDraft(institution, {
                            sketchfabImportLimit: Number(event.target.value || 0),
                          })
                        }
                        disabled={savingId === institution._id}
                      />
                    </td>
                    <td>
                      <input
                        aria-label={`Active ${institution.name}`}
                        type="checkbox"
                        checked={draft.active}
                        onChange={(event) => updateDraft(institution, { active: event.target.checked })}
                        disabled={savingId === institution._id}
                      />
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${styles.darkActionButton}`}
                        onClick={() => handleSave(institution)}
                        disabled={
                          savingId === institution._id ||
                          !hasChanges ||
                          draft.name.trim().length < 2 ||
                          draft.edmProviderName.trim().length < 2 ||
                          !Number.isInteger(draft.sketchfabImportLimit) ||
                          draft.sketchfabImportLimit < 1 ||
                          draft.sketchfabImportLimit > 250
                        }
                      >
                        Save
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
